/**
 * R2-5 — WHAT THE TAB SAYS.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * WHY THIS FILE EXISTS
 *
 * `frontend/index.html` used to carry the only `<title>` in the repo, and
 * `src/app.ts` serves that one file for every route. So the hub, Doodle Duel,
 * the calendar and all eight daily challenges all said "Go Team Williams Trivia" —
 * in the tab, in the browser's history list, in the share sheet, and, the one
 * that actually matters here, in the name a phone pre-fills when someone adds
 * the page to their home screen. This family texts and QRs these links
 * constantly, so a wrong title is not cosmetic: it is what the link is CALLED
 * everywhere it gets passed on.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * ONE WRITER
 *
 * `<RouteTitle/>` is mounted ONCE, inside `<Router>` and above `<Routes>`, and
 * it is the only thing in the app that assigns `document.title`. Above
 * `<Routes>` means it sees every navigation the router makes, including the
 * `<Navigate>` alias redirects (`/party` → `/doodleduel`, the catch-all → `/`),
 * which never reach the server and so could never have been titled by a static
 * file. Pages do not get to set their own title — a second writer is how you
 * end up with a title that depends on which effect ran last.
 *
 * `titleFor` is a PURE function of the pathname, which is what makes it
 * testable without a browser and what keeps the title from flashing: it does
 * not wait for `GET /api/daily-challenges/games` to come back, so it never has to
 * render a placeholder first. The game's display name therefore comes from
 * `daily-challenges/names.ts` — a leaf module, deliberately NOT the board registry,
 * because this file is eager and the boards are a 164 kB lazy chunk. That file's
 * header has the numbers; its names are pinned to the server registry by a unit
 * test, so they cannot drift into a title nobody re-reads.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * THE VOICE
 *
 * Brand first, em dash, then where you are: `Go Team Williams — Queens`. Brand
 * first because a truncated tab (a phone shows ~15 characters) should still say
 * whose app this is, and because it matches the chrome's own
 * "Go Team Williams — home". The hub is the bare brand: `/` is not a section.
 */

import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { dailyChallengeName } from "../daily-challenges/names";
import { formatPuzzleDate } from "../daily-challenges/format";

export const BRAND = "Go Team Williams";

/** `Go Team Williams — X`, or the bare brand when there is no section. */
function brandTitle(section?: string | null): string {
  return section ? `${BRAND} — ${section}` : BRAND;
}

/** The same `YYYY-MM-DD` shape `DailyChallengesPage` accepts; anything else is a typo. */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The tab title for a pathname. Pure, total, and never throws — an unrecognised
 * path gets the bare brand, which is also what the catch-all route redirects to.
 */
export function titleFor(pathname: string): string {
  // A trailing slash is the same place; case is NOT normalised, because the
  // router does not normalise it either — `/daily-challenges/Queens` renders the
  // coming-soon page, so it gets the coming-soon page's title.
  const path = pathname.replace(/\/+$/, "") || "/";

  // THE ALIAS PATHS ARE DELIBERATELY ABSENT. `/party` and `/trivia-night` are
  // `<Navigate replace>` routes: they render, their effect fires, and the
  // location is the real path before anything is painted — so they inherit
  // their destination's title on the next tick without this function having to
  // know they exist. Listing them would also duplicate the alias table, and
  // F26's sweep (`no client file still builds a user-facing /party link`) is
  // right to object to a second copy of a path that has one owner.
  if (path === "/") return brandTitle();
  if (path === "/trivia") return brandTitle("Trivia");
  if (path === "/doodleduel") return brandTitle("Doodle Duel");
  // § F45 — Game Table used to be a second origin with its own <title> ("Game
  // Table", brand-less, because it was its own product). It is a section of
  // this app now, so it wears the app's voice like every other section.
  if (path === "/gametable") return brandTitle("Game Table");
  // § F170/§ F171 — the two categories added 2026-09-17. The duel screens are
  // all "Wordle": a match id in a tab title tells nobody anything, and the
  // brand-first voice has to survive a 15-character truncation.
  if (path === "/group-games") return brandTitle("Group Games");
  // § F179 — the category is "Head To Head" (it was "Turn-Based Games" for a day).
  if (path === "/head-to-head") return brandTitle("Head To Head");
  if (path === "/head-to-head/wordle" || /^\/head-to-head\/wordle\/[^/]+$/.test(path)) {
    return brandTitle("Wordle");
  }
  // § F198 — Head To Head Doodle.
  if (path === "/head-to-head/doodle" || /^\/head-to-head\/doodle\/[^/]+$/.test(path)) {
    return brandTitle("Doodle");
  }

  // § F220 — Puzzles, and its one kind so far.
  if (path === "/puzzles") return brandTitle("Puzzles");
  if (path === "/puzzles/pencil-pointers" || /^\/puzzles\/pencil-pointers\/[^/]+$/.test(path)) {
    return brandTitle("Pencil Pointers");
  }

  if (path === "/daily-challenges") return brandTitle("Daily Challenges");
  if (path === "/daily-challenges/history") return brandTitle("Daily Challenges History");
  if (path === "/daily-challenges/calendar") return brandTitle("Daily Challenges Calendar");
  if (path === "/daily-challenges/rankings") return brandTitle("Daily Challenges Rankings");

  const day = path.match(/^\/daily-challenges\/day\/(.+)$/)?.[1];
  if (day) {
    // A malformed date in the URL is a typo, and `DailyChallengesPage` sends a typo
    // to today's tiles — so the title says the same thing that page will.
    return ISO_DATE.test(day) ? brandTitle(`Daily Challenges, ${formatPuzzleDate(day)}`) : brandTitle("Daily Challenges");
  }

  const gameId = path.match(/^\/daily-challenges\/([^/]+)$/)?.[1];
  if (gameId) {
    // An id this build does not know is a typo, and a typo renders the
    // section's own fallback page — so it gets the section's title rather than
    // a tab that reads "Go Team Williams — not-a-real-game".
    return brandTitle(dailyChallengeName(gameId) ?? "Daily Challenges");
  }

  return brandTitle();
}

/**
 * Renders nothing; keeps `document.title` equal to `titleFor(pathname)`.
 *
 * Mounted once in `App`, inside `<Router>`. Keyed on the pathname alone: the
 * query string and the hash are not part of where you are for titling purposes
 * (`/daily-challenges/queens?date=…` is still Queens), and including them would retitle
 * on every `?code=` change in a party lobby for no gain.
 */
export function RouteTitle(): null {
  const { pathname } = useLocation();
  useEffect(() => {
    document.title = titleFor(pathname);
  }, [pathname]);
  return null;
}
